import React, { useCallback, useMemo, useState } from 'react';

import { link } from '@aline/routes';
import { Typography } from '@mui/material';
import type { GridColDef, GridRenderCellParams, GridSortModel } from '@mui/x-data-grid-premium';
import { DataGridPremium } from '@mui/x-data-grid-premium';
import { formatDistanceToNow } from 'date-fns';
import { Link, useNavigate } from 'react-router-dom';
import {
  AlineTooltip,
  allOwnerFilters,
  FiltersToolbarButton,
  getIsOwnerCellValue,
  getStringOperatorsWithSafeIsAnyOf,
  ListCountFooter,
  ListEmpty,
  OwnerCell,
  ReportFiltersPanel,
  SearchInput,
  Spacer,
  useListToolbarStyles,
} from 'src/components';
import { GridAlineTooltip } from 'src/components/generic/data-grid/grid-aline-tooltip';
import { Apollo } from 'src/gql/generated';
import {
  createDataGridPremiumPaperStyles,
  FiltersAnchorProvider,
  useDataGridSearch,
  useFiltersAnchorState,
} from 'src/hooks';
import { makeStyles } from 'tss-react/mui';

import { NewSkillButton } from './new-skill-button';
import { SkillAccessCell } from './skill-access-cell';
import { SkillDraftBadge, SkillPublishBadge } from './skill-draft-badge';
import { SKILL_SCOPE_LABELS } from './skill-scope-labels';

type SkillRow = Apollo.AgentSkillListItemFragment;
type SkillColDef = GridColDef<SkillRow>;

const MIN_NAME_CELL_WIDTH_PX = 350;

const useStyles = makeStyles()(theme => ({
  listContainer: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    minHeight: 0,
    position: 'relative',
  },
  name: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
    minWidth: 0,
  },
  nameLink: {
    color: 'inherit',
    textDecoration: 'none',
  },
  xGrid: {
    '& .MuiDataGrid-row': {
      cursor: 'pointer',
    },
    flexGrow: 1,
    minHeight: 0,
  },
}));

/**
 * The skills library, laid out like the playbooks list: filters, search and "+" on top, one
 * row per skill, and a click on a row opens it. Renaming and deleting live in the editor.
 */
export const SkillsList = React.memo(function SkillsList(): JSX.Element {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const { anchorEl: filtersAnchorEl, setAnchorEl: setFiltersAnchorEl } = useFiltersAnchorState();
  const { data, error, loading } = Apollo.useGetAgentSkillsQuery({
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });
  const skills = useMemo(() => data?.agentSkills ?? [], [data?.agentSkills]);
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: 'updatedAt' satisfies keyof SkillRow, sort: 'desc' },
  ]);
  const openSkill = useCallback(
    ({ row }: { row: SkillRow }) => navigate(link.AdminAgentSkillDetail({ skillId: row.id })),
    [navigate]
  );
  const columnDefs = useMemo(
    (): SkillColDef[] => [
      {
        field: 'name',
        filterOperators: getStringOperatorsWithSafeIsAnyOf(),
        filterable: true,
        flex: 2.5,
        headerName: 'Name',
        minWidth: MIN_NAME_CELL_WIDTH_PX,
        renderCell: NameCell,
        type: 'string',
        // Filter, sort and search on the name the cell shows, so "Untitled skill" matches.
        valueGetter: (_value, row) => getSkillName(row.name),
      },
      {
        field: 'owner',
        filterOperators: allOwnerFilters,
        filterable: true,
        flex: 1,
        getApplyQuickFilterFn: (value: string) => cellValue =>
          getIsOwnerCellValue(cellValue) &&
          typeof cellValue.displayName === 'string' &&
          cellValue.displayName.toLowerCase().includes(value.toLowerCase()),
        headerName: 'Author',
        minWidth: 180,
        renderCell: AuthorCell,
        sortComparator: (v1, v2) => {
          const v1Name = getIsOwnerCellValue(v1) ? (v1.displayName ?? '') : '';
          const v2Name = getIsOwnerCellValue(v2) ? (v2.displayName ?? '') : '';
          return v1Name.localeCompare(v2Name);
        },
        type: 'string',
      },
      {
        field: 'scope',
        flex: 1,
        headerName: 'Access',
        minWidth: 130,
        renderCell: params => <SkillAccessCell {...params} />,
        type: 'singleSelect',
        valueGetter: (_value, row) => SKILL_SCOPE_LABELS[row.scope],
        valueOptions: Object.values(SKILL_SCOPE_LABELS),
      },
      {
        field: 'updatedAt',
        flex: 1,
        headerName: 'Last updated',
        minWidth: 150,
        renderCell: UpdatedAtCell,
        type: 'dateTime',
        valueFormatter: (value: Date) => formatDistanceToNow(value, { addSuffix: true }),
        valueGetter: (_value, row) => new Date(row.updatedAt),
      },
    ],
    []
  );

  return (
    <FiltersAnchorProvider setAnchorEl={setFiltersAnchorEl}>
      <div className={classes.listContainer}>
        <DataGridPremium
          disableColumnMenu
          disableColumnResize
          disableRowSelectionOnClick
          showToolbar
          className={classes.xGrid}
          columns={columnDefs}
          // Only while nothing is cached: the background refetch keeps the rows already shown.
          loading={loading && !data}
          rows={skills}
          slotProps={{
            filterPanel: {
              filterableColumns: 'visible',
            },
            footer: {
              noun: 'skills',
              totalCount: skills.length,
            },
            loadingOverlay: {
              noRowsVariant: 'linear-progress',
              variant: 'linear-progress',
            },
            panel: {
              placement: 'bottom-start',
              target: filtersAnchorEl ?? undefined,
            },
          }}
          slots={{
            baseTooltip: GridAlineTooltip,
            filterPanel: ReportFiltersPanel,
            footer: ListCountFooter,
            // A failed load is not an empty library.
            noRowsOverlay: error ? SkillsListLoadError : SkillsListEmpty,
            toolbar: SkillsListToolbar,
          }}
          sortModel={sortModel}
          sx={createDataGridPremiumPaperStyles}
          onCellClick={openSkill}
          onSortModelChange={setSortModel}
        />
      </div>
    </FiltersAnchorProvider>
  );
});

function getSkillName(name: string): string {
  return name || 'Untitled skill';
}

function NameCell({ hasFocus, row }: GridRenderCellParams<SkillRow>): JSX.Element {
  const { classes } = useStyles();
  const stopPropagation = useCallback((event: React.MouseEvent) => event.stopPropagation(), []);
  return (
    <div className={classes.name}>
      <Typography
        noWrap
        className={classes.nameLink}
        component={Link}
        data-cy="admin-skills-row"
        tabIndex={hasFocus ? 0 : -1}
        to={link.AdminAgentSkillDetail({ skillId: row.id })}
        onClick={stopPropagation}
      >
        {getSkillName(row.name)}
      </Typography>
      {!row.isComplete && <SkillDraftBadge description={row.description} name={row.name} />}
      <SkillPublishBadge
        hasUnpublishedChanges={row.hasUnpublishedChanges}
        isPublished={row.isPublished}
      />
    </div>
  );
}

function AuthorCell({ row }: GridRenderCellParams<SkillRow>): JSX.Element {
  return <OwnerCell showName row={{ owner: row.owner }} />;
}

function SkillsListEmpty(): JSX.Element {
  return <ListEmpty subtitle="Create your first skill to get started." title="No skills" />;
}

function SkillsListLoadError(): JSX.Element {
  return <ListEmpty subtitle="Refresh the page to try again." title="Could not load skills" />;
}

function SkillsListToolbar(): JSX.Element {
  const { classes } = useListToolbarStyles();
  const { search, setSearch } = useDataGridSearch();

  return (
    <div className={classes.toolbar}>
      <FiltersToolbarButton />
      <Spacer />
      <SearchInput placeholder="Search skills..." search={search} setSearch={setSearch} />
      <NewSkillButton />
    </div>
  );
}

function UpdatedAtCell({ row }: GridRenderCellParams<SkillRow>): JSX.Element {
  return (
    <AlineTooltip arrow placement="top" title={new Date(row.updatedAt).toLocaleString()}>
      <span>{formatDistanceToNow(new Date(row.updatedAt), { addSuffix: true })}</span>
    </AlineTooltip>
  );
}
